import React, {useState} from 'react';

import {
    Mail,
    Lock,
    Eye,
    EyeOff,
    CheckCircle,
    AlertCircle,
    ArrowRight,
    Home,
    Loader
} from 'lucide-react';
import {useNavigate, useLocation} from "react-router-dom";
import {useAuth} from "../../App";
import SEO from "../../Components/Common/SEO";

const Login = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const {login} = useAuth();
    const redirectTo = location.state?.from
        ? `${location.state.from.pathname}${location.state.from.search || ''}`
        : '/';

    const handleNavigate = (path) => {
        navigate(path);
    };

    const [formData, setFormData] = useState({
        email: '',
        password: '',
        rememberMe: false
    });

    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [loginStatus, setLoginStatus] = useState(null);
    const [errors, setErrors] = useState({});

    const handleInputChange = (e) => {
        const {name, value, type, checked} = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.email) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email';
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsLoading(true);
        setLoginStatus(null);
        setErrors({});

        try {
            // Authentication is by email/password only - the account's role
            // is determined and returned by the backend, never selected
            // by the client. Submitting a role hint here previously let a
            // user's session come back with the wrong role.
            await login({
                email: formData.email,
                password: formData.password,
                rememberMe: formData.rememberMe
            });

            setLoginStatus('success');
            setTimeout(() => handleNavigate(redirectTo), 1000);
        } catch (error) {
            setLoginStatus('error');

            if (error.status === 401) {
                setErrors({general: 'Invalid email or password. Please try again.'});
            } else if (error.status === 403) {
                setErrors({general: 'Account is disabled. Please contact support.'});
            } else if (error.status === 429) {
                setErrors({general: 'Too many login attempts. Please try again later.'});
            } else if (error.field) {
                setErrors({[error.field]: error.fieldMessage || error.message});
            } else if (error.status) {
                setErrors({general: error.message || 'Login failed. Please try again.'});
            } else {
                setErrors({general: 'Network error. Please check your connection and try again.'});
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-karibu-100 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
            <SEO title="Sign In" canonicalPath="/login" noindex />
            {/* Background Pattern */}
            <div className="fixed inset-0 z-0">
                <div className="absolute inset-0 bg-gradient-to-br from-highland-50 to-karibu-200" />
                <div className="absolute inset-0 opacity-10" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                }} />
            </div>

            <div className="relative z-10 sm:mx-auto sm:w-full sm:max-w-md">
                <div className="flex items-center justify-center mb-4">
                    <div className="h-10 w-10 bg-highland-500 rounded-xl flex items-center justify-center mr-3">
                        <Home className="h-5 w-5 text-white"/>
                    </div>
                    <div className="text-left">
                        <h1 className="font-display text-xl font-bold text-soil-900 leading-tight">Welcome Back</h1>
                        <p className="text-xs text-soil-600">Sign in to your Rent Utopia account</p>
                    </div>
                </div>
            </div>

            <div className="relative z-10 mt-2 sm:mx-auto sm:w-full sm:max-w-md">
                <div className="bg-white py-6 px-4 shadow-xl rounded-xl sm:px-8">
                    {loginStatus === 'success' && (
                        <div className="mb-4 p-2.5 bg-highland-50 border border-highland-100 rounded-lg flex items-center space-x-2">
                            <CheckCircle className="w-4 h-4 text-highland-500 flex-shrink-0"/>
                            <p className="text-sm text-highland-600">Login successful! Redirecting...</p>
                        </div>
                    )}

                    {loginStatus === 'error' && (
                        <div className="mb-4 p-2.5 bg-murram-100/40 border border-murram-100 rounded-lg flex items-center space-x-2">
                            <AlertCircle className="w-4 h-4 text-murram-500 flex-shrink-0"/>
                            <p className="text-sm text-murram-600">
                                {errors.general || 'Invalid email or password. Please try again.'}
                            </p>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Email Field */}
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-soil-600 mb-1">
                                Email Address
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Mail className="h-4 w-4 text-soil-400"/>
                                </div>
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    className={`block w-full pl-10 pr-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent transition-colors ${
                                        errors.email ? 'border-murram-500' : 'border-soil-100'
                                    }`}
                                    placeholder="Enter your email"
                                />
                            </div>
                            {errors.email && (
                                <p className="mt-1 text-xs text-murram-600">{errors.email}</p>
                            )}
                        </div>

                        {/* Password Field */}
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-soil-600 mb-1">
                                Password
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Lock className="h-4 w-4 text-soil-400"/>
                                </div>
                                <input
                                    id="password"
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    autoComplete="current-password"
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    className={`block w-full pl-10 pr-10 py-2.5 border rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent transition-colors ${
                                        errors.password ? 'border-murram-500' : 'border-soil-100'
                                    }`}
                                    placeholder="Enter your password"
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? (
                                        <EyeOff className="h-4 w-4 text-soil-400"/>
                                    ) : (
                                        <Eye className="h-4 w-4 text-soil-400"/>
                                    )}
                                </button>
                            </div>
                            {errors.password && (
                                <p className="mt-1 text-xs text-murram-600">{errors.password}</p>
                            )}
                        </div>

                        {/* Remember Me & Forgot Password */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <input
                                    id="rememberMe"
                                    name="rememberMe"
                                    type="checkbox"
                                    checked={formData.rememberMe}
                                    onChange={handleInputChange}
                                    className="h-4 w-4 text-highland-500 focus:ring-highland-500 border-soil-100 rounded"
                                />
                                <label htmlFor="rememberMe" className="ml-2 block text-sm text-soil-600">
                                    Remember me
                                </label>
                            </div>

                            <button
                                type="button"
                                onClick={() => handleNavigate('/forgot-password')}
                                className="text-sm font-medium text-highland-500 hover:text-highland-600"
                            >
                                Forgot password?
                            </button>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="group relative w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-highland-500 hover:bg-highland-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-highland-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                        >
                            {isLoading ? (
                                <>
                                    <Loader className="w-4 h-4 mr-2 animate-spin"/>
                                    Signing in...
                                </>
                            ) : (
                                <>
                                    Sign in
                                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-200"/>
                                </>
                            )}
                        </button>
                    </form>

                    {/* Sign Up Link */}
                    <div className="mt-4 text-center">
                        <p className="text-sm text-soil-600">
                            Don't have an account?{' '}
                            <button
                                onClick={() => handleNavigate('/register')}
                                className="font-medium text-highland-500 hover:text-highland-600"
                            >
                                Sign up for free
                            </button>
                        </p>
                    </div>
                </div>

                {/* Back to Home */}
                <div className="mt-4 text-center">
                    <button
                        onClick={() => handleNavigate('/')}
                        className="inline-flex items-center text-sm text-soil-400 hover:text-soil-600"
                    >
                        <ArrowRight className="w-3.5 h-3.5 mr-1 rotate-180"/>
                        Back to Home
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Login;
